import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './income.style.js';
import '@cof/fs-currency-field-wc-beta/fs-currency-field.js';
import '@cof/fs-radio-wc/fs-radio.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiPersonalIncomeComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepIncome');
    this.values = {};
    this.coAppPrefix = '';
    this.incomeId = '';
    this.additionalAmount = false;
    this.helperText = '';
    this.isIconic = false;
    this.application = {};
  }

  static styles = [style, globalStyle];

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    invalidIncome: { type: Boolean, reflect: true },
    helperText: { type: String, attribute: 'helper-text', reflect: true },
    additionalAmount: {
      type: Boolean,
      attribute: 'additional-amount',
      reflect: true,
    },
    incomeAmount: { type: String, attribute: 'income-amount', reflect: true },
    otherIncome: { type: String, attribute: 'other-income', reflect: true },
    isIconic: { type: Boolean, attribute: 'is-iconic', reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
    this.application = ApplicationService.Application;
    this.PQV2 = ApplicationService.isIconicV2;
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    const suffix = this.additionalAmount ? 'Other' : '';
    this.incomeId = `${this.coAppPrefix}incomeAmount${suffix}`;
    this.incomeFieldName = this.additionalAmount
      ? 'otherIncome'
      : 'incomeAmount';

    this.updateValues(
      this.incomeFieldName,
      this.application[this.isPrimary ? 'primaryApplicant' : 'coApplicant'][
        this.incomeFieldName
      ] ?? ''
    );

    this.updateValues(this.frequencyFieldName, 'Annual');

    if (this.PQV2) {
      this.resetValues();
    }
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.values.incomeAmount =
      this.application[this.isPrimary ? 'primaryApplicant' : 'coApplicant']
        .incomeAmount ?? '';
    const appOtherIncome =
      this.application[this.isPrimary ? 'primaryApplicant' : 'coApplicant']
        .otherIncome;
    if (appOtherIncome > 0) {
      this.values.otherIncome = appOtherIncome;
    } else {
      this.values.otherIncome = '';
    }

    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: 'incomeAmount',
          isValid: this.values.incomeAmount !== '',
        },
      })
    );
    if (this.additionalAmount) {
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'otherIncome',
            isValid: this.values.otherIncome !== '',
          },
        })
      );
    }

    this.requestUpdate();
  }

  updateValues(fieldName, value) {
    // Updates the value and triggers rerender
    // Format the value to round the value and set to positive value
    this.values[fieldName] = value;
    this.requestUpdate();
  }

  renderIncomeFieldTitle() {
    if (this.additionalAmount) {
      return 'Additional income';
    }
    if (!this.isPrimary && this.PQV2) {
      return 'How much do they make a year (before taxes)?';
    }
    if (!this.isPrimary) {
      return `How much does ${this.application.coApplicant.firstName} make a year (before taxes)?`;
    }
    return 'How much do you make a year (before taxes)?';
  }

  render() {
    return html`
      <div>
        <div class="personal__income">
          <fs-currency-field
            class="${this.isIconic ? 'iconic-currency-field' : ''}"
            id="${this.incomeId}"
            name="${this.incomeFieldName}"
            min="1"
            max="10000000"
            decimal-separator="none"
            maximum-fraction-digits="0"
            minimum-decimal-percision="0"
            autocomplete="on"
            ?required="${!this.additionalAmount}"
            value="${this.values[this.incomeFieldName]}"
            @input="${(ev) => {
              this.changed(
                this.incomeFieldName,
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              );
            }}"
            @blur="${(ev) => {
              this.changed(
                this.incomeFieldName,
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              );
              if (ev.target.validity.valid && !this.PQV2) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: {
                    value: `${this.coAppPrefix}${this.incomeFieldName}`,
                  },
                });
              }
              if (!ev.target.validity.valid && !this.PQV2) {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  {
                    replacements: {
                      value: 'Amount entered should be greater than $0',
                    },
                  }
                );
              }
            }}"
            value-missing-message="Please enter a valid income"
          >
            ${this.renderIncomeFieldTitle()}
          </fs-currency-field>
        </div>
        ${!this.additionalAmount
          ? html`<fs-body-text
              size="tiny"
              id="calculated-income-helper-text"
              class=${this.PQV2 ? 'PQv2-income-spacing' : ''}
            >
              ${`Approx ${this.calculateWeeklyIncome(
                this.values.incomeAmount
              )}/week or ${this.calculateMonthlyIncome(
                this.values.incomeAmount
              )}/month`}
            </fs-body-text>`
          : nothing}
        <fs-helper-text
          type="primary"
          helper="${this.helperText}"
          ?hidden="${!this.helperText}"
          id="income-helper-text"
        ></fs-helper-text>
      </div>
    `;
  }

  changed(name, ev, value = ev.target?.value) {
    if (ev.target.validity.valid) {
      // entered income is stored in values. annual income is calculated and stored in step component
      this.updateValues(this.incomeFieldName, +value);
    }
    if (this.PQV2) {
      if (this.additionalAmount) {
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
          replacements: { value: `${this.coAppPrefix}otherIncome` },
        });
      } else {
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
          replacements: { value: `${this.coAppPrefix}incomeAmount` },
        });
      }
    }
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: ev.target?.id,
          isValid: ev.target.validity.valid,
        },
      })
    );
  }

  calculateWeeklyIncome(annualIncome) {
    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(annualIncome / 52);
  }

  calculateMonthlyIncome(annualIncome) {
    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(annualIncome / 12);
  }
}

customElements.define('refi-personal-income', RefiPersonalIncomeComponent);
